import { Injectable } from '@angular/core';
import { DashboardResponse, ResumenDetalladoResponse } from '../models/dashboard.model';
import { TareaSemana, HistorialSemana, DiaHistorial, ProyectoHistorial, RegistroHistorial, ProyectoResumen, WeekRange, PaceStatus } from '../models/ui.model';
import { RegistroTiempoResponse, RegistroTiempoGroupResponse, RegistroProyectoGroup } from '../models/registro-tiempo.model';
import { getWeekRangeForDate } from './week-utils';

const PROJECT_COLORS = [
  '#4F46E5', '#0EA5E9', '#10B981', '#F59E0B',
  '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6',
  '#F97316', '#6366F1', '#06B6D4', '#84CC16'
];

@Injectable({
  providedIn: 'root'
})
export class SelfSpaceAdapterService {

  adaptTareas(hitos: any[], clienteMap?: Map<number, string>): TareaSemana[] {
    if (!hitos || !Array.isArray(hitos)) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return hitos
      // Mantener solo hitos con alguna fecha, pero sin filtrar por día laborable,
      // para que también aparezcan tareas que terminan en fin de semana.
      .filter((h: any) => {
        const endDate = h.hito?.fechaFechaFin || h.fechaFin || h.hito?.fechaInicio || h.fechaInicio;
        return !!endDate;
      })
      .map((h: any) => {
        const fechaInicioStr = h.hito?.fechaInicio || h.fechaInicio || '';
        const fechaFinStr = h.hito?.fechaFechaFin || h.fechaFin || '';
        const fStr = fechaFinStr || fechaInicioStr;
        const [fy, fm, fd] = (fStr.split('T')[0] || fStr).split('-').map(Number);
        const fechaFin = new Date(fy, fm - 1, fd);
        const horasEstimadas = h.duracionHoras || h.horasEstimadas || 0;
        const horasUsadas = h.costoActualHoras || h.horasUsadas || 0;
        const porcentaje = horasEstimadas > 0 ? Math.round((horasUsadas / horasEstimadas) * 100) : 0;

        const valorPlaneadoHoras = h.valorPlaneadoHoras || 0;
        const valorPlaneadoHoyHoras = h.valorPlaneadoHoyHoras || 0;
        const porcentajeEsperado = valorPlaneadoHoras > 0
          ? Math.round((valorPlaneadoHoyHoras / valorPlaneadoHoras) * 100)
          : 0;
        const porcentajeAvance = h.porcentajeAvance ?? h.hito?.porcentajeAvance ?? 0;
        const paceStatus = this.calcPaceStatus(porcentajeAvance, porcentajeEsperado, valorPlaneadoHoras);

        return {
          id: h.id,
          nombre: h.hito?.nombre || h.nombre || '',
          proyectoId: h.proyecto?.id || h.proyectoId || 0,
          proyectoNombre: h.proyecto?.nombre || h.proyectoNombre || '',
          clienteNombre: clienteMap?.get(h.proyecto?.id || h.proyectoId || 0) || 'Sin cliente',
          fechaInicio: fechaInicioStr,
          fechaFin: fechaFinStr,
          horasEstimadas,
          horasUsadas,
          porcentajePresupuesto: porcentaje,
          porcentajeAvance,
          valorPlaneadoHoras,
          valorPlaneadoHoyHoras,
          porcentajeEsperado,
          paceStatus,
          atrasada: fechaFin < today
        };
      });
  }

  adaptHistorial(groups: RegistroTiempoGroupResponse[], projectNameMap?: Map<number, string>): HistorialSemana {
    if (!groups || !Array.isArray(groups)) return { dias: [] };

    const dias: DiaHistorial[] = groups
      .map(group => {
        // Backend returns "day" as Instant (e.g. "2026-03-24T00:00:00Z"), extract date part
        const fecha = group.day ? group.day.split('T')[0] : '';
        // Group registerTimes by project — use "id|name" as key to avoid collapsing different projects with null IDs
        const proyectoMap = new Map<string, { proyectoId: number; proyectoNombre: string; registros: RegistroTiempoResponse[] }>();
        for (const r of (group.registerTimes || [])) {
          const pid = r.proyectoId || 0;
          const pname = r.proyecto || (pid && projectNameMap?.get(pid)) || ((r.tareaOrganizacionalId || r.categoriaImprevistoId) ? 'Imprevistos' : 'Sin proyecto');
          const key = pid ? String(pid) : `0|${pname}`;
          if (!proyectoMap.has(key)) {
            proyectoMap.set(key, { proyectoId: pid, proyectoNombre: pname, registros: [] });
          }
          proyectoMap.get(key)!.registros.push(r);
        }
        return {
          fecha,
          fechaFormateada: fecha ? this.formatDateSpanish(fecha) : '',
          totalHoras: group.totalHours || 0,
          proyectos: Array.from(proyectoMap.values()).map(p => this.adaptProyectoHistorial(p))
        };
      })
      // Mostrar también registros de fin de semana en el historial;
      // solo filtramos entradas sin fecha válida.
      .filter(g => !!g.fecha)
      .sort((a, b) => b.fecha.localeCompare(a.fecha));

    return { dias };
  }

  adaptProyectosResumen(resumen: ResumenDetalladoResponse): ProyectoResumen[] {
    if (!resumen?.proyectos) return [];

    return resumen.proyectos.map((p, i) => ({
      proyectoId: p.proyectoId,
      proyectoNombre: p.proyectoNombre,
      horas: p.horas,
      porcentaje: p.porcentaje,
      color: PROJECT_COLORS[i % PROJECT_COLORS.length]
    }));
  }

  adaptRegistroForm(form: any, email: string): any {
    return {
      email,
      fechaInicio: form.fechaInicio,
      fechaFin: form.fechaFin,
      duracion: form.duracionMinutos / 60,
      duracionMinutos: form.duracionMinutos,
      hitoId: form.hitoId || null,
      hito: form.hito || null,
      proyectoId: form.proyectoId || null,
      proyecto: form.proyecto || null,
      tareaOrganizacionalId: form.tareaOrganizacionalId || null,
      categoriaImprevistoId: form.categoriaImprevistoId || null,
      descripcion: form.descripcion || ''
    };
  }

  getWeekRange(date: Date): WeekRange {
    return getWeekRangeForDate(date);
  }

  navigateWeek(currentWeek: WeekRange, direction: 'prev' | 'next'): WeekRange {
    const [y, m, d] = currentWeek.startDate.split('-').map(Number);
    const start = new Date(y, m - 1, d);
    const offset = direction === 'prev' ? -7 : 7;
    start.setDate(start.getDate() + offset);
    return this.getWeekRange(start);
  }

  private adaptProyectoHistorial(p: RegistroProyectoGroup | { proyectoId: number; proyectoNombre: string; registros: RegistroTiempoResponse[] }): ProyectoHistorial {
    return {
      proyectoId: p.proyectoId,
      proyectoNombre: p.proyectoNombre,
      registros: (p.registros || []).map(r => this.adaptRegistroHistorial(r, p))
    };
  }

  private adaptRegistroHistorial(r: RegistroTiempoResponse, p: RegistroProyectoGroup | { proyectoId: number; proyectoNombre: string }): RegistroHistorial {
    const horaRegistro = (r.createdDate && this.formatTime(r.createdDate)) || r.horaInicio || this.formatTime(r.fechaInicio);
    return {
      id: r.id,
      actividad: r.hito || ((r.tareaOrganizacionalId || r.categoriaImprevistoId) ? 'Imprevisto' : 'Sin nombre'),
      duracionMinutos: r.duracionMinutos,
      categoria: '',
      // Prefer createdDate (momento real de registro); luego horaInicio explícita y por último fechaInicio
      hora: horaRegistro,
      proyectoId: p.proyectoId,
      proyectoNombre: p.proyectoNombre,
      clienteNombre: r.cliente || 'Sin cliente',
      hitoId: r.hitoId,
      tareaOrganizacionalId: r.tareaOrganizacionalId,
      categoriaImprevistoId: r.categoriaImprevistoId,
      fechaInicio: r.fechaInicio,
      descripcion: r.descripcion || ''
    };
  }

  private calcPaceStatus(avance: number, esperado: number, valorPlaneadoHoras: number): PaceStatus {
    if (!valorPlaneadoHoras || !esperado) return 'unknown';
    const ratio = esperado > 0 ? avance / esperado : 1;
    if (ratio >= 1) return 'on-track';
    if (ratio >= 0.75) return 'at-risk';
    return 'behind';
  }

  private isWeekday(dateStr: string): boolean {
    if (!dateStr) return true;
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const day = date.getDay();
    return day >= 1 && day <= 5;
  }

  private toISODate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatWeekLabel(start: Date, end: Date): string {
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const startStr = `${start.getDate()} ${months[start.getMonth()]}`;
    const endStr = `${end.getDate()} ${months[end.getMonth()]} ${end.getFullYear()}`;
    return `${startStr} - ${endStr}`;
  }

  private formatDateSpanish(dateStr: string): string {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  }

  private formatTime(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }
}
